import ReactPlayer from "react-player";
import { showToast } from "@callveriq/utils/toast";
import {
    Box,
    CircularProgress,
    Paper,
    Slider,
    SliderValueLabelProps,
    Tooltip,
    Typography,
    styled,
    useTheme,
} from "@mui/material";

import { formatTimeHHMMSS } from "@callveriq/utils/helper/date.helper";
import { useEffect, useState } from "react";
import useMediaPlayerContext from "../../hooks/useMediaPlayerContext";
import {
    checkFileType,
    extractFileType,
    isDarkMode,
    isDefined,
} from "@callveriq/utils/helper/common.helper";
import poster from "@callveriq/assets/images/jpeg/media_player_poster.jpg";
import witelable_poster from "@callveriq/assets/images/png/whitelabel_poster.png";
import {
    PlaySvg,
    ExpandPlayerSvg,
    CollapsePlayerSvg,
} from "./MediaPlayerIcons";
import MediaPlayerSettings from "./MediaPlayerSettings";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import ClosedCaptionDisabledIcon from "@mui/icons-material/ClosedCaptionDisabled";
import { useGetDomainConfigQuery } from "@callveriq/redux/services/domain/domain.service";
import { pxToRem } from "@callveriq/utils/getFontValue";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";

const StyledPlayerOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isExpanded" && prop !== "loaded",
})<{ isExpanded: boolean; loaded: number }>(
    ({ isExpanded, loaded, theme }) => ({
        color: isExpanded ? "white" : theme.palette.textColors["666"],
        position: "absolute",
        boxSizing: "border-box",
        top: 0,
        width: "100%",
        borderRadius: isExpanded ? "0px 0px 12px 12px" : "30px",
        opacity: isExpanded ? 0 : 1,
        transition: "opacity 0.2s ease-in-out",
        // maxHeight: "112px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        "& .MuiSlider-root": {
            height: "6px",
            background: isExpanded
                ? "rgba(255, 255, 255, 0.40)"
                : "rgba(153, 153, 153, 0.20)",
        },
        "& .MuiSlider-rail": {
            background: isExpanded
                ? "rgba(255, 255, 255, 0.40)"
                : "rgba(153, 153, 153, 0.20)",
            position: "relative",
            "::after": {
                content: "''",
                width: `${loaded * 100}%`, // loaded %
                height: "inherit",
                background: isExpanded
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(153, 153, 153, 0.40)",
                display: "block",
                borderRadius: "30px",
            },
        },
        "& .MuiSlider-track": {
            background: isExpanded ? "white" : theme.palette.primary.main,
        },
        "& .MuiSlider-thumb": {
            border: "none",
        },
    })
);

function ValueLabelComponent(props: SliderValueLabelProps) {
    const { children, value } = props;

    return (
        <Tooltip enterTouchDelay={0} placement="top" title={value}>
            {children}
        </Tooltip>
    );
}

export default function MediaPlayer({
    mediaUrl,
    subtitles,
    height,
    canDownload = true,
    isSnippet = false,
    startTime,
    endTime,
    defaultIsExpanded = true,
    autoPauseOnVisibilityHidden = false,
}: {
    mediaUrl?: string;
    subtitles?: string;
    height?: number;
    canDownload?: boolean;
    startTime?: number;
    endTime?: number;
    isSnippet?: boolean;
    defaultIsExpanded?: boolean;
    autoPauseOnVisibilityHidden?: boolean;
}) {
    const { data: domainConfig } = useGetDomainConfigQuery();
    const {
        playerRef,
        playerState,
        handlePreview,
        handlePlayPause,
        handlePlay,
        handlePause,
        handleEnded,
        handleProgress,
        handleDuration,
        handlePlayerControlSliderSeek,
        seekBy10seconds,
        toggleCaptions,
        togglePip,
        handleVolumeChange,
        handleEnablePip,
        toggleMute,
        hideController,
        disableSeek,
        hideButtons,
    } = useMediaPlayerContext();
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultIsExpanded);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showVolumeSlider, setShowVolumeSlider] = useState<boolean>(false);
    const [isSliderActive, setIsSliderActive] = useState<boolean>(false);

    // Handle showing the volume slider
    const handleShowVolumeSlider = () => {
        setShowVolumeSlider(true);
    };

    // Handle potentially hiding the volume slider
    const handleHideVolumeSlider = () => {
        // Only hide if we're not currently interacting with the slider
        if (!isSliderActive) {
            setTimeout(() => {
                if (!isSliderActive) {
                    setShowVolumeSlider(false);
                }
            }, 1000);
        }
    };

    function calculateDuration(value: number) {
        return formatTimeHHMMSS(value);
    }

    function calculateVolume(value: number) {
        return `${Math.floor(value * 100)}%`;
    }

    //For enabling and disabling subtitles
    useEffect(() => {
        if (!isDefined(playerRef.current)) return;
        if (
            !isDefined(
                playerRef?.current?.getInternalPlayer()?.textTracks?.[0]?.mode
            )
        )
            return;
        if (playerState.captionsEnabled) {
            playerRef.current.getInternalPlayer().textTracks[0].mode =
                "showing";
        } else {
            playerRef.current.getInternalPlayer().textTracks[0].mode = "hidden";
        }
    }, [playerState.captionsEnabled]);

    useEffect(() => {
        return () => {
            playerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (playerState.isFullscreen) {
            setIsExpanded(true);
        }
    }, [playerState.isFullscreen]);

    useEffect(() => {
        if (!autoPauseOnVisibilityHidden) {
            return;
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                handlePause();
            }
        };
        window.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, []);

    const isVideo = checkFileType(extractFileType(mediaUrl ?? "")) === "video";
    const theme = useTheme();
    return (
        <Box
            className="w-[100%] relative react-player-container"
            sx={{
                ...(!isVideo && {
                    "& .react-player--picture__in__option": {
                        display: "none",
                    },
                }),
                overflow: showVolumeSlider ? "visible" : "hidden",
                ...(!isExpanded
                    ? {
                          video: {
                              visibility: "hidden",
                          },
                          height: "54px",
                          borderRadius: disableSeek ? "6px" : "30px",
                          //Disable seek is for coaching clips
                      }
                    : {
                          height: `${height || 260}px`,
                          bgcolor: (theme) =>
                              isDarkMode(theme) ? "background.default" : "#121",
                          ...(!disableSeek && {
                              borderRadius: "12px",
                          }),
                          "&:hover": {
                              ".video-player__controls": {
                                  opacity: 1,
                              },
                          },
                      }),

                transition: "all 0.3s ease",
            }}
        >
            {isLoading && isExpanded && (
                <Box
                    component="button"
                    sx={{
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        color: "white",
                    }}
                    className="flex items-center justify-center absolute"
                >
                    <CircularProgress color="inherit" />
                </Box>
            )}
            {!isLoading && isExpanded && !playerState.playing && (
                <Box
                    component="button"
                    sx={{
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        bgcolor: "rgba(255, 255, 255, 0.40)",
                        width: "40px",
                        height: "40px",
                        borderRadius: "100%",
                        color: "white",
                        zIndex: 100,
                    }}
                    className="flex items-center justify-center absolute"
                    onClick={handlePlay}
                >
                    <PlaySvg />
                </Box>
            )}

            <ReactPlayer
                className="react-player"
                height={"100%"}
                width={"100%"}
                url={mediaUrl}
                ref={playerRef}
                config={{
                    file: {
                        forceVideo: true,
                        attributes: {
                            poster: !isDefined(domainConfig?.logo)
                                ? poster
                                : witelable_poster,
                            // crossOrigin is required for subtitles but can cause
                            // network errors if the media server lacks CORS headers
                            ...(isDefined(subtitles) && {
                                crossOrigin: "anonymous",
                            }),
                        },
                        ...(isDefined(subtitles) && {
                            tracks: [
                                {
                                    kind: "captions",
                                    src: subtitles!,
                                    default: true,
                                    srcLang: "en",
                                    label: "Subtitles",
                                },
                            ],
                        }),
                    },
                }}
                onError={(e) => {
                    const mediaError = (
                        e as Event & { target: HTMLVideoElement }
                    )?.target?.error;
                    let message =
                        "Player has encountered an error. Please try refreshing the page.";
                    if (mediaError) {
                        switch (mediaError.code) {
                            case MediaError.MEDIA_ERR_NETWORK:
                                message =
                                    "A network error caused the media download to fail.";
                                break;
                            case MediaError.MEDIA_ERR_DECODE:
                                message =
                                    "The media could not be decoded. The file may be corrupt.";
                                break;
                            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                                message =
                                    "The media format is not supported by your browser.";
                                break;
                            default:
                                break;
                        }
                    }
                    showToast({ type: "error", message });
                    setIsLoading(false);
                }}
                controls={playerState.controls}
                pip={playerState.pip}
                loop={playerState.loop}
                muted={playerState.muted}
                playing={playerState.playing}
                playbackRate={playerState.playbackRate}
                progressInterval={100}
                volume={playerState.volume}
                onPlay={handlePlay}
                onEnded={handleEnded}
                onPause={handlePause}
                onDuration={handleDuration}
                onProgress={handleProgress}
                onClickPreview={handlePreview}
                onEnablePIP={handleEnablePip}
                onDisablePIP={togglePip}
                onBuffer={() => {
                    setIsLoading(true);
                }}
                onBufferEnd={() => {
                    setIsLoading(false);
                }}
                onReady={() => {
                    setIsLoading(false);
                }}
                onSeek={(e) => {
                    if (disableSeek) {
                        // Check if the seek is forward (to a later position)
                        const currentTime = playerState.playedSeconds;
                        const seekTime = Math.floor(e);

                        // If seeking to a later position, reload the page
                        if (seekTime > currentTime) {
                            window.location.reload();
                        }
                    }
                }}
            />
            <StyledPlayerOverlay
                isExpanded={isExpanded}
                loaded={playerState.loaded}
                sx={{
                    ...(!playerState.playing && {
                        opacity: 1,
                    }),
                }}
                className="video-player__controls"
                onClick={() => {
                    if (isLoading) return;
                    handlePlayPause();
                }}
            >
                <Box className="relative">
                    <Box
                        className="absolute bottom-0 left-0 w-full"
                        sx={(theme) => ({
                            background: isExpanded
                                ? !isDarkMode(theme)
                                    ? "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 20.14%, #333 91.18%)"
                                    : "transparent"
                                : isDarkMode(theme)
                                ? "background.paper"
                                : disableSeek
                                ? "background.default"
                                : "#F5F5F5",
                            borderRadius: isExpanded
                                ? "0px 0px 12px 12px"
                                : "30px",
                            ...(disableSeek &&
                                !isExpanded && {
                                    borderRadius: "6px",
                                    border: "1px solid rgba(255, 255, 255, 0.77)",
                                    background: "rgba(255, 255, 255, 0.63)",
                                    boxShadow:
                                        "0px 2px 12px 0px rgba(26, 98, 242, 0.06)",
                                    backdropFilter: "blur(8px)",
                                }),
                        })}
                        component={isDarkMode(theme) ? Paper : "div"}
                    >
                        <Box
                            className="flex items-end justify-between gap-[8px]"
                            sx={{
                                m: 4,
                                ...(!isExpanded && { mb: 1.5 }),
                                ...(disableSeek &&
                                    !isExpanded && { mx: 1, mt: 1.5 }),
                            }}
                        >
                            <Box
                                className="flex justify-start items-center  flex-1"
                                gap={1.5}
                            >
                                {hideButtons?.rewind ? null : (
                                    <Box
                                        component="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            seekBy10seconds("back");
                                        }}
                                    >
                                        <Replay10Icon
                                            sx={{ fontSize: pxToRem(16) }}
                                        />
                                    </Box>
                                )}

                                <Box
                                    component={"button"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlayPause();
                                    }}
                                    className="play-pause-button"
                                >
                                    {playerState.playing ? (
                                        <PauseIcon
                                            sx={{ fontSize: pxToRem(16) }}
                                        />
                                    ) : (
                                        <PlayArrowIcon
                                            sx={{ fontSize: pxToRem(16) }}
                                        />
                                    )}
                                </Box>

                                {hideButtons?.fastForward ||
                                disableSeek ? null : (
                                    <Box
                                        component="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            seekBy10seconds("forward");
                                        }}
                                    >
                                        <Forward10Icon
                                            sx={{ fontSize: pxToRem(16) }}
                                        />
                                    </Box>
                                )}

                                <Typography
                                    variant="small"
                                    sx={{
                                        color: isExpanded
                                            ? "white"
                                            : "textColors.333",
                                        mt: 0.25,
                                    }}
                                >
                                    {`${formatTimeHHMMSS(
                                        isSnippet &&
                                            isDefined(endTime) &&
                                            isDefined(startTime)
                                            ? Math.max(
                                                  playerState.playedSeconds -
                                                      startTime,
                                                  0
                                              )
                                            : playerState.playedSeconds
                                    )}/${formatTimeHHMMSS(
                                        isSnippet &&
                                            isDefined(endTime) &&
                                            isDefined(startTime)
                                            ? endTime - startTime
                                            : playerState.duration
                                    )}`}
                                </Typography>
                                {hideController ? null : (
                                    <Box
                                        className="flex-1"
                                        sx={{
                                            px: 0.5,
                                            pl: 1,
                                            height: "28px",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <Slider
                                            size="small"
                                            value={playerState?.playedSeconds}
                                            valueLabelDisplay="auto"
                                            min={0}
                                            max={playerState?.duration}
                                            step={1}
                                            sx={{
                                                p: 0,
                                                top: 0,
                                                ...(disableSeek && {
                                                    "& .MuiSlider-thumb": {
                                                        display: "block",
                                                    },
                                                }),
                                            }}
                                            onChange={
                                                handlePlayerControlSliderSeek
                                            }
                                            valueLabelFormat={calculateDuration}
                                            slots={{
                                                valueLabel: ValueLabelComponent,
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box
                                className="flex flex-shrink-0 justify-evenly items-end gap-2 -mt-0.5 relative "
                                sx={{
                                    ...(disableSeek && {
                                        width: "32px",
                                    }),
                                }}
                            >
                                <Box
                                    className=" w-8  flex items-center flex-col justify-end transition-all duration-300  "
                                    sx={(theme) => ({
                                        ...{
                                            p: 1,
                                            position: disableSeek
                                                ? "absolute"
                                                : "relative",
                                            height: showVolumeSlider
                                                ? "115px"
                                                : "32px",
                                            pb: 0.5,
                                            bgcolor: disableSeek
                                                ? "rgba(235,235,235)"
                                                : isExpanded
                                                ? "rgba(227, 227, 227, 0.20)"
                                                : isDarkMode(theme)
                                                ? "background.paper"
                                                : "#F5F5F5",
                                            borderRadius: "30px",
                                            transition:
                                                "width 0.2s ease-in-out",
                                            overflow: "visible", // Make sure the slider thumb is visible when dragging
                                        },
                                    })}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseEnter={handleShowVolumeSlider}
                                    onMouseOver={handleShowVolumeSlider}
                                    onMouseLeave={handleHideVolumeSlider}
                                >
                                    {showVolumeSlider && (
                                        <Box
                                            className=" absolute z-[10000]"
                                            onMouseEnter={
                                                handleShowVolumeSlider
                                            }
                                            onMouseOver={handleShowVolumeSlider}
                                            sx={{
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                bottom: pxToRem(32),
                                            }}
                                        >
                                            <Slider
                                                size="small"
                                                orientation="vertical"
                                                value={playerState?.volume}
                                                valueLabelDisplay="auto"
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                sx={{
                                                    p: 0,
                                                    height: "60px !important",
                                                    width: "6px !important",

                                                    "& .MuiSlider-thumb": {
                                                        width: 16,
                                                        height: 16,
                                                    },
                                                }}
                                                onMouseDown={() =>
                                                    setIsSliderActive(true)
                                                }
                                                onMouseUp={() =>
                                                    setIsSliderActive(false)
                                                }
                                                onChange={handleVolumeChange}
                                                valueLabelFormat={
                                                    calculateVolume
                                                }
                                                onMouseEnter={
                                                    handleShowVolumeSlider
                                                }
                                                onMouseMove={
                                                    handleShowVolumeSlider
                                                }
                                                slots={{
                                                    valueLabel:
                                                        ValueLabelComponent,
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {hideButtons?.volume ? null : (
                                        <Box
                                            component={"button"}
                                            onClick={toggleMute}
                                            onMouseEnter={
                                                handleShowVolumeSlider
                                            }
                                            onMouseOver={handleShowVolumeSlider}
                                            onMouseLeave={
                                                handleHideVolumeSlider
                                            }
                                        >
                                            {playerState.muted ? (
                                                <VolumeOffIcon
                                                    sx={{
                                                        fontSize: pxToRem(16),
                                                        ...(disableSeek &&
                                                            showVolumeSlider && {
                                                                fill: theme
                                                                    .palette
                                                                    .primary
                                                                    .main,
                                                            }),
                                                    }}
                                                />
                                            ) : (
                                                <VolumeUpIcon
                                                    sx={{
                                                        fontSize: pxToRem(16),
                                                        ...(disableSeek &&
                                                            showVolumeSlider && {
                                                                fill: theme
                                                                    .palette
                                                                    .primary
                                                                    .main,
                                                            }),
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                {hideController ? (
                                    <></>
                                ) : (
                                    <>
                                        {isExpanded && isDefined(subtitles) && (
                                            <Box
                                                component="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCaptions();
                                                }}
                                            >
                                                {playerState.captionsEnabled ? (
                                                    <ClosedCaptionIcon />
                                                ) : (
                                                    <ClosedCaptionDisabledIcon />
                                                )}
                                            </Box>
                                        )}
                                        {hideButtons?.settings ? null : (
                                            <MediaPlayerSettings
                                                canDownload={canDownload}
                                            />
                                        )}
                                        {hideButtons?.expand ? null : (
                                            <Box
                                                component="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsExpanded(
                                                        (prev) => !prev
                                                    );
                                                }}
                                                className="expand-player-button"
                                            >
                                                {isExpanded ? (
                                                    <CollapsePlayerSvg />
                                                ) : (
                                                    <ExpandPlayerSvg />
                                                )}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </StyledPlayerOverlay>
        </Box>
    );
}
